import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, Play, TestTube } from "lucide-react";
import { SANDBOX_AGENTS } from "@/data/agents";
import AgentChat from "@/components/AgentChat";
import { useLanguage } from "@/contexts/LanguageContext";
import { translate } from "@/utils/translations";

const Sandbox = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const { language } = useLanguage();

  const filteredAgents = SANDBOX_AGENTS.filter(agent =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (selectedAgent) {
    return <AgentChat agent={selectedAgent} onBack={() => setSelectedAgent(null)} />;
  }

  return (
    <div className="space-y-6" data-testid="sandbox-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{translate("Sandbox Agents", language)}</h1>
          <p className="text-gray-400">{translate("Testing and validation agents for humanoid robots", language)}</p>
        </div>
        <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
          <TestTube className="w-3 h-3 mr-1" />
          {SANDBOX_AGENTS.length} {translate("Validators", language)}
        </Badge>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search sandbox agents..."
          className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
        />
      </div>

      {/* Agent Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredAgents.map((agent) => (
          <Card
            key={agent.id}
            className="bg-[#0A0A0A] border-white/10 hover:border-cyan-500/30 transition-all group cursor-pointer"
            onClick={() => setSelectedAgent(agent)}
          >
            <CardContent className="p-6">
              {/* Icon and Status */}
              <div className="flex items-start justify-between mb-4">
                <div className="text-4xl">{agent.icon}</div>
                <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-xs">
                  {agent.status === "production" ? "Production" : "Experimental"}
                </Badge>
              </div>

              {/* Name and Description */}
              <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                {translate(agent.name, language)}
              </h3>
              <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                {translate(agent.retailExplanation, language)}
              </p>

              {/* Category and Version */}
              <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                <span>{agent.category}</span>
                <span>{agent.version}</span>
              </div>

              {/* Action Button */}
              <Button
                className="w-full bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/20 hover:border-cyan-500/40"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedAgent(agent);
                }}
              >
                <Play className="w-4 h-4 mr-2" />
                {translate("Run Validation", language)}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredAgents.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No sandbox agents found</p>
        </div>
      )}
    </div>
  );
};

export default Sandbox;
